import React, { useEffect, useMemo, useState } from 'react';
import { guildImg } from '../../utils/guildImage';

/**
 * **거점 현황 — 시간 바를 움직이며 보는 점령전 지도** (사용자 2026-10-13).
 *
 * 서버 `GET /api/siege-battles/map-timeline?siegeId=` 의 기록(게임 지도 패킷을 30초마다 받아 거점 모양이 바뀔 때만 한 장)
 * 으로 그 시각의 거점 39곳을 그린다: 누구 것인지(길드 색), 남은 방덱(칸 다섯), 공사 중(남은 시간), 본진과 끊김(⊘),
 * 싸우는 중(붉은 고리). 위에는 그 시각의 세 길드 점수·거점 수, 아래는 거점이 넘어간 일 목록.
 *
 * **자리는 게임 지도와 같지 않다** — 게임 그림의 1~12 번호와 패킷의 거점 번호가 단순히 맞지 않아서(실측), 세 길드 본진을
 * 게임처럼 왼쪽 아래(pos 1)·위(pos 3)·오른쪽 아래(pos 2)에 두고 그 지역 12곳을 번호 순으로 본진 둘레에 늘어놓는다.
 * 번호는 사이트 전투기록의 "N번 거점" 과 같은 번호다. 색도 게임처럼 pos 1 파랑 · pos 2 노랑 · pos 3 빨강.
 */

interface MapBase {
    n: number; type: number; guild: number; status: number; built: number; hit: number;
    decks: number; down: number; fight: number; atk: number; won?: number; cut: number; vis: number;
}
interface Snap { at: number; until: number; bases: MapBase[]; scores: Array<{ guildId: number; score: number }>; scoresEnd: Array<{ guildId: number; score: number }> }
interface MatchInfo {
    siegeId: number; matchId: number; matchStart: number; matchFinish: number;
    restStart?: number; restFinish?: number; maxScore: number;
    guilds: Array<{ guildId: number; posId: number; name: string }>;
}

/**
 * **화면 자리(side)** — 게임은 **보는 길드를 늘 왼쪽 아래(파랑)** 에 두고 지도를 돌려 그린다(10/9 판 스샷과 패킷으로 확인:
 * 설탕이 pos 3 인데 왼쪽 아래). side = (pos − 우리 pos) mod 3 + 1 → 1 왼쪽 아래 파랑 · 2 오른쪽 아래 노랑 · 3 위 빨강.
 * 아래 색·모양·자리 표는 모두 side 가 열쇠다.
 */
const POS_COLOR: Record<number, string> = { 1: '#3b82f6', 2: '#eab308', 3: '#ef4444' };
const POS_DARK: Record<number, string> = { 1: '#1e40af', 2: '#a16207', 3: '#b91c1c' };
/** 기록 사이가 이만큼 넘게 벌어지면 "기록 없음" (지도 패킷은 30초마다) */
const GAP = 90;

const posOfBase = (n: number) => (n <= 13 ? 1 : n <= 26 ? 2 : 3);

// ── 자리 ────────────────────────────────────────────────────────────────────
const W = 970, H = 525;

/**
 * **게임 지도의 거점 자리** (970×525 지도 기준, 거점 그림 가운데) — swgt.io 점령전 시뮬레이터 지도에서 옮겨 왔다
 * (2026-10-13). 첫 열쇠는 화면 자리(side 1 왼쪽 아래 · 2 오른쪽 아래 · 3 위), 둘째는 **게임 지도에 적힌 번호**(지역 안
 * 1~12, 0 은 본진). 패킷의 지역은 pos 마다 13칸(pos 1 → 1~13 …)이고, 그 지역이 어느 side 에 그려지는지는 우리 pos 로 정한다.
 */
const SPOT: Record<number, Array<[number, number]>> = {
    1: [[66, 478], [31, 353], [160, 414], [248, 502], [222, 341], [302, 405], [90, 268], [204, 251], [306, 273], [410, 298], [418, 378], [461, 458], [378, 506]],
    2: [[904, 478], [939, 351], [808, 415], [705, 492], [663, 384], [769, 351], [535, 505], [577, 442], [533, 379], [554, 304], [666, 278], [773, 265], [888, 259]],
    3: [[491, 39], [637, 105], [493, 112], [321, 106], [568, 158], [411, 144], [792, 176], [700, 196], [601, 223], [487, 235], [375, 217], [284, 191], [185, 175]]
};
/**
 * **패킷 차례 → 게임 지도 번호.** 패킷의 거점 번호를 지역 안 차례(번호 − 지역 첫 번호)로 놓으면 게임 지도 번호와
 * 대부분 같지만 **2↔9 · 7↔8 · 11↔12 가 서로 바뀌어 있다** — 휴전 중 게임 화면의 공사 남은 시간(05:11 · 16:19 · 16:59 ·
 * 19:37 · 21:44 · 29:28 · 30:20 · 31:18)을 패킷의 `construct_time − 휴전 끝` 과 초까지 맞대어 확인했다(2026-10-13).
 */
const GAME_NO = [0, 1, 9, 3, 4, 5, 6, 8, 7, 2, 10, 12, 11];
/** 게임 지도에 적힌 번호 (본진 0) */
const label = (n: number) => GAME_NO[n - (posOfBase(n) - 1) * 13 - 1] ?? 0;

const hhmm = (t: number) => new Date(t * 1000).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
const dayTime = (t: number) => new Date(t * 1000).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
const mmss = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = Math.floor(s % 60);
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${m}:${String(ss).padStart(2, '0')}`;
};

/**
 * 공사 남은 시간 — 휴전 동안은 공사가 멈춘다. 패킷의 완료 시각(`construct_time`)은 휴전 길이까지 더한 값이라
 * 휴전과 겹치는 만큼을 뺀다(휴전 중 게임 화면의 타이머 = 완료 시각 − 휴전 끝, 실측).
 */
function buildLeft(built: number, t: number, m: { restStart?: number; restFinish?: number }): number {
    let left = built - t;
    if (m.restStart && m.restFinish) left -= Math.max(0, Math.min(built, m.restFinish) - Math.max(t, m.restStart));
    return left;
}

/** 그 점령전에 거점 지도 기록이 있나 — 전투기록 화면의 [거점 현황] 탭을 보일지 */
/**
 * **꺼 둠**(사용자 2026-10-13) — 기록은 게임이 점령전 지도 화면에 떠 있을 때만 쌓여서 군데군데 빈다. 다시 켜려면 true.
 * 플러그인의 기록 쌓기와 서버 API 는 그대로 둔다.
 */
const SIEGE_MAP_ENABLED = false;

export function useSiegeMapAvailable(siegeId?: number): boolean {
    const [has, setHas] = useState(false);
    useEffect(() => {
        setHas(false);
        if (!SIEGE_MAP_ENABLED || !siegeId) return;
        let gone = false;
        fetch(`/api/siege-battles/map-timeline?siegeId=${siegeId}&head=1`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => { if (!gone) setHas(!!j?.found); })
            .catch(() => {});
        return () => { gone = true; };
    }, [siegeId]);
    return has;
}

/** 한 장에서 바뀐 것 한 줄 */
interface Change { n: number; kind: 'take' | 'down' | 'won' | 'fight' | 'built' | 'cut' | 'link' | 'decks'; from?: number; to?: number; count?: number; atk?: number }

/** 이웃한 두 장 사이에 바뀐 것 — 점령 · 덱 깨짐 · 방어 성공 · 공격 시작 · 공사 끝 · 끊김/이어짐 · 방덱 배치 */
function diffSnaps(prev: Snap | null, cur: Snap): Change[] {
    if (!prev) return [];
    const before = new Map(prev.bases.map((b) => [b.n, b]));
    const out: Change[] = [];
    for (const b of cur.bases) {
        const p = before.get(b.n);
        if (!p) continue;
        if (p.guild !== b.guild) { out.push({ n: b.n, kind: 'take', from: p.guild, to: b.guild }); continue; }
        if (b.down > p.down) out.push({ n: b.n, kind: 'down', count: b.down - p.down, to: b.guild });
        if ((b.won || 0) > (p.won || 0)) out.push({ n: b.n, kind: 'won', count: (b.won || 0) - (p.won || 0), to: b.guild });
        if (b.fight > 0 && p.fight === 0) out.push({ n: b.n, kind: 'fight', atk: b.atk, to: b.guild });
        if (p.status === 1 && b.status === 2) out.push({ n: b.n, kind: 'built', to: b.guild });
        if (b.cut !== p.cut) out.push({ n: b.n, kind: b.cut ? 'cut' : 'link', to: b.guild });
        if (b.decks !== p.decks && b.down === p.down) out.push({ n: b.n, kind: 'decks', count: b.decks, to: b.guild });
    }
    // 점령이 먼저, 그다음 깨짐·공격 순
    const order = { take: 0, down: 1, fight: 2, won: 3, built: 4, cut: 5, link: 6, decks: 7 };
    return out.sort((a, b) => order[a.kind] - order[b.kind] || a.n - b.n);
}

/**
 * **바는 시간이 아니라 기록 한 장씩** 움직인다(사용자 2026-10-13) — 한 장 = 거점 모양이 바뀐 한 번. 칸마다 그 장의 시각과
 * "이 기록에서 바뀐 것" 을 보여 준다.
 */
export default function SiegeMapTimeline({ siegeId, compact }: { siegeId: number; compact: boolean }) {
    const [data, setData] = useState<{ match: MatchInfo; snaps: Snap[]; ownGuildId: number } | null>(null);
    const [err, setErr] = useState('');
    const [i, setI] = useState(0);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        let gone = false;
        fetch(`/api/siege-battles/map-timeline?siegeId=${siegeId}`)
            .then((r) => r.json())
            .then((j) => {
                if (gone) return;
                if (!j?.success || !j.found) { setErr('이 점령전의 거점 기록이 없습니다'); return; }
                setData({ match: j.match, snaps: j.snaps, ownGuildId: Number(j.ownGuildId) || 0 });
                setI(Number.MAX_SAFE_INTEGER);  // 마지막 칸 (칸 수는 아래에서 센다)
            })
            .catch(() => { if (!gone) setErr('거점 기록을 받지 못했습니다'); });
        return () => { gone = true; };
    }, [siegeId]);

    const snaps = useMemo(() => data?.snaps || [], [data]);
    const match = data?.match;

    const guildPos = useMemo(() => {
        const m = new Map<number, { posId: number; name: string }>();
        (match?.guilds || []).forEach((g) => m.set(g.guildId, { posId: g.posId, name: g.name }));
        return m;
    }, [match]);
    // 우리 길드가 왼쪽 아래가 되게 돌린다 — 우리가 이 판에 없으면 pos 1 이 왼쪽 아래
    const ownPos = (match?.guilds || []).find((g) => g.guildId === data?.ownGuildId)?.posId || 1;
    const sideOfPos = (p: number) => ((p - ownPos + 3) % 3) + 1;
    const sideOfGuild = (gid: number) => sideOfPos(guildPos.get(gid)?.posId || 0);
    const spotOf = (n: number) => SPOT[sideOfPos(posOfBase(n))][label(n)];
    const colorOf = (gid: number) => POS_COLOR[sideOfGuild(gid)] || '#999';
    const nameOf = (gid?: number) => (gid ? guildPos.get(gid)?.name || '' : '');
    /** 거점이 있는 지역의 본래 주인(그 지역 본진의 길드) */
    const regionName = (n: number) => (match?.guilds || []).find((g) => g.posId === posOfBase(n))?.name || '';

    /** 장마다 바뀐 것 · 점령이 있었던 장 */
    const changes = useMemo(() => snaps.map((s, k) => diffSnaps(k > 0 ? snaps[k - 1] : null, s)), [snaps]);
    /** 칸 = 바뀐 일이 있는 장만(첫 장 포함) — 모양이 같은 장은 이벤트로 치지 않는다(사용자) */
    const steps = useMemo(() => snaps.map((_, k) => k).filter((k) => k === 0 || changes[k].length > 0), [snaps, changes]);
    const takes = useMemo(() => {
        const out: Array<{ k: number; c: Change }> = [];
        steps.forEach((k, j) => changes[k].forEach((c) => { if (c.kind === 'take') out.push({ k: j, c }); }));
        return out;
    }, [steps, changes]);
    const last = Math.max(0, steps.length - 1);

    // 재생 — 0.7초마다 한 장
    useEffect(() => {
        if (!playing) return;
        const id = window.setInterval(() => {
            setI((cur) => {
                if (cur >= last) { setPlaying(false); return last; }
                return cur + 1;
            });
        }, 700);
        return () => window.clearInterval(id);
    }, [playing, last]);

    if (err) return <div style={{ padding: 30, textAlign: 'center', color: '#999', fontWeight: 600 }}>{err}</div>;
    if (!data || !match || !snaps.length) return <div style={{ padding: 30, textAlign: 'center', color: '#999', fontWeight: 600 }}>거점 기록을 불러오는 중…</div>;

    const idx = Math.min(i, last);
    const k = steps[idx];
    const snap = snaps[k];
    const t = snap.at;
    const prevSnap = k > 0 ? snaps[k - 1] : null;
    const gapBefore = prevSnap && snap.at - prevSnap.until > GAP ? [prevSnap.until, snap.at] : null;
    const inRest = !!(match.restStart && match.restFinish && t >= match.restStart && t < match.restFinish);
    const now = changes[k];

    const score = (gid: number) => snap.scores.find((s) => s.guildId === gid)?.score || 0;
    const towers = (gid: number) => snap.bases.filter((b) => b.type !== 1 && b.guild === gid).length;
    const guilds = [...match.guilds].sort((a, b) => sideOfPos(a.posId) - sideOfPos(b.posId));
    const pctOf = (k: number) => `${last > 0 ? (k / last) * 100 : 0}%`;
    const go = (k: number) => { setPlaying(false); setI(Math.max(0, Math.min(last, k))); };
    const changed = Array.from(new Set(now.map((c) => c.n)));

    const where = (n: number) => (
        <span style={{ fontWeight: 800, color: POS_DARK[sideOfPos(posOfBase(n))], whiteSpace: 'nowrap' }} title={`전투기록 거점 번호 ${n}`}>
            {regionName(n)} {label(n)}번
        </span>
    );
    const gname = (gid?: number) => <span style={{ fontWeight: 800, color: POS_DARK[sideOfGuild(gid || 0)] }}>{nameOf(gid)}</span>;
    const sentence = (c: Change) => {
        switch (c.kind) {
            case 'take': return <>{where(c.n)} · {gname(c.from)} → {gname(c.to)} <b>점령</b></>;
            case 'down': return <>{where(c.n)} · {gname(c.to)} 방덱 {c.count}개 깨짐</>;
            case 'won': return <>{where(c.n)} · {gname(c.to)} 방어 성공{c.count && c.count > 1 ? ` ${c.count}번` : ''}</>;
            case 'fight': return <>{where(c.n)} · {gname(c.atk)} 공격 시작</>;
            case 'built': return <>{where(c.n)} · {gname(c.to)} 공사 끝</>;
            case 'cut': return <>{where(c.n)} · 본진과 끊김</>;
            case 'link': return <>{where(c.n)} · 본진과 다시 이어짐</>;
            default: return <>{where(c.n)} · {gname(c.to)} 방덱 {c.count}개 배치</>;
        }
    };

    return (
        <div>
            {/* 세 길드 — 점수(상한 대비)·거점 수 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: compact ? 4 : 10 }}>
                {guilds.map((g) => {
                    const sc = score(g.guildId);
                    return (
                        <div key={g.guildId} style={{ borderRadius: 10, padding: compact ? '5px 6px' : '8px 10px', background: '#f8f7fc', border: `1.5px solid ${POS_COLOR[sideOfPos(g.posId)]}55`, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                <img src={guildImg(g.name)} alt="" style={{ width: compact ? 18 : 24, height: compact ? 18 : 24, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }}
                                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                <span style={{ fontWeight: 800, fontSize: compact ? 11 : 14, color: POS_DARK[sideOfPos(g.posId)], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                                <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: compact ? 10 : 12, color: '#666', whiteSpace: 'nowrap' }}>거점 {towers(g.guildId)}</span>
                            </div>
                            <div style={{ marginTop: 4, height: compact ? 6 : 8, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(100, (sc / (match.maxScore || 20000)) * 100)}%`, height: '100%', background: POS_COLOR[sideOfPos(g.posId)] }} />
                            </div>
                            <div style={{ marginTop: 2, fontSize: compact ? 10 : 12, fontWeight: 800, color: '#444' }}>{Math.floor(sc).toLocaleString()}</div>
                        </div>
                    );
                })}
            </div>

            {/* 지도 */}
            <div style={{ position: 'relative', marginTop: compact ? 6 : 10, borderRadius: 14, overflow: 'hidden', border: '1px solid #5a3d4a' }}>
                {/* 바탕은 swgt.io 시뮬레이터의 점령전 지도 그림 그대로(`/img/siege-map-bg.jpg`, 970×525 — 거점 자리도 이 그림 기준) */}
                <svg viewBox={`0 0 ${W} ${H + 6}`} style={{ display: 'block', width: '100%', height: 'auto', background: '#3a2a30' }}>
                    <style>{`@keyframes sm-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.25 } }`}</style>
                    <image href="/img/siege-map-bg.jpg" x={0} y={0} width={W} height={H} preserveAspectRatio="none" />
                    {/* 이 기록에서 바뀐 거점 — 흰 고리 */}
                    {changed.map((n) => {
                        const [x, y] = towerCenter(spotOf(n), compact ? 1.25 : 1);
                        return <circle key={`c${n}`} cx={x} cy={y} r={(compact ? 1.25 : 1) * 27} fill="rgba(255,255,255,0.22)" stroke="#fff" strokeWidth={2.5} />;
                    })}
                    {snap.bases.map((b) => <BaseMark key={b.n} b={b} at={spotOf(b.n)} regionSide={sideOfPos(posOfBase(b.n))} left={buildLeft(b.built, t, match)} k={compact ? 1.25 : 1} color={colorOf(b.guild)} atkColor={colorOf(b.atk)}
                        ownerSide={sideOfGuild(b.guild)} name={guildPos.get(b.guild)?.name || ''} />)}
                </svg>
                {(gapBefore || inRest) && (
                    <div style={{
                        position: 'absolute', left: '50%', top: 8, transform: 'translateX(-50%)', padding: '4px 12px', borderRadius: 99,
                        background: 'rgba(30, 20, 40, 0.75)', color: '#fff', fontSize: compact ? 11 : 12, fontWeight: 700, whiteSpace: 'nowrap'
                    }}>
                        {inRest ? '휴전 시간' : `앞 기록과 사이 ${hhmm(gapBefore![0])}~${hhmm(gapBefore![1])} 기록 없음`}
                    </div>
                )}
            </div>

            {/* 기록 바 — 한 칸 = 기록 한 장 */}
            <div style={{ marginTop: compact ? 8 : 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                    <button onClick={() => go(idx - 1)} disabled={idx <= 0} style={ctrl(compact)} title="앞 기록">◀</button>
                    <button onClick={() => { if (idx >= last) setI(0); setPlaying((p) => !p); }} style={{ ...ctrl(compact), minWidth: compact ? 34 : 40 }}>{playing ? '⏸' : '⏵'}</button>
                    <button onClick={() => go(idx + 1)} disabled={idx >= last} style={ctrl(compact)} title="다음 기록">▶</button>
                    <span style={{ fontWeight: 900, fontSize: compact ? 12 : 14, color: '#333', marginLeft: 4 }}>{dayTime(t)}</span>
                    <span style={{ fontWeight: 700, fontSize: compact ? 11 : 12, color: '#999' }}>{idx + 1} / {steps.length}</span>
                    <button onClick={() => go(last)} style={{ ...ctrl(compact), marginLeft: 'auto' }}>마지막</button>
                </div>
                <div style={{ position: 'relative', height: 28 }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 11, height: 6, borderRadius: 99, background: '#ececf3' }} />
                    {/* 점령이 있었던 기록 — 새 주인 색 눈금 */}
                    {takes.map(({ k, c }, j) => (
                        <div key={j} style={{ position: 'absolute', top: 4, width: 3, height: 20, marginLeft: -1, left: pctOf(k), background: colorOf(c.to || 0), opacity: 0.9, borderRadius: 1 }} />
                    ))}
                    <input
                        type="range" min={0} max={last} step={1} value={idx}
                        onChange={(e) => { setPlaying(false); setI(Number(e.target.value)); }}
                        style={{ position: 'absolute', inset: 0, width: '100%', margin: 0, background: 'transparent', accentColor: '#667eea', cursor: 'pointer' }}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: compact ? 10 : 11, color: '#999', fontWeight: 600 }}>
                    <span>{dayTime(snaps[0].at)}</span><span>{dayTime(snaps[snaps.length - 1].until)}</span>
                </div>
            </div>

            {/* 이 기록에서 바뀐 것 */}
            <div style={{ marginTop: compact ? 6 : 10, padding: compact ? '6px 8px' : '8px 12px', borderRadius: 10, background: '#f8f7fc', fontSize: compact ? 11 : 13, lineHeight: 1.7, color: '#444' }}>
                <div style={{ fontWeight: 800, color: '#555', marginBottom: 2 }}>이 기록에서 바뀐 것</div>
                {idx === 0 ? <div style={{ color: '#999' }}>첫 기록입니다</div> : now.map((c, j) => <div key={j}>{sentence(c)}</div>)}
            </div>

            {/* 점령 목록 — 누르면 그 기록으로 */}
            {takes.length > 0 && (
                <div style={{ marginTop: compact ? 8 : 12 }}>
                    <div style={{ fontWeight: 800, fontSize: compact ? 12 : 13, color: '#555', marginBottom: 4 }}>거점이 넘어간 일 {takes.length}건</div>
                    <div style={{ maxHeight: compact ? 150 : 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {[...takes].reverse().map(({ k, c }, j) => (
                            <button key={j} onClick={() => go(k)} style={{
                                display: 'flex', gap: 8, alignItems: 'center', textAlign: 'left', border: 'none', cursor: 'pointer', borderRadius: 8,
                                padding: compact ? '4px 6px' : '5px 8px', background: k === idx ? '#eef2ff' : 'transparent', fontSize: compact ? 11 : 12
                            }}>
                                <span style={{ color: '#888', fontWeight: 700, width: 42 }}>{hhmm(snaps[steps[k]].at)}</span>
                                <span>{sentence(c)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const ctrl = (compact: boolean): React.CSSProperties => ({
    border: '1px solid #ddd', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: compact ? 12 : 13, fontWeight: 800,
    padding: compact ? '3px 8px' : '4px 10px', color: '#555'
});

/**
 * **거점 그림 — 게임 화면에서 오려 낸 것**(`/img/siege-towers/<종류>_<색>.webp`, 2026-10-13 MuMu 지도 화면).
 * swgt 지도(=게임 지도 그림)를 화면에 맞춰 겹친 뒤(1.919배) 탑 둘레를 GrabCut 으로 오렸다. 본진(hq)과 일반 탑(tower)만 쓴다.
 * 값 = [거점 자리에서 왼쪽 위 모서리까지 x, y, 폭, 높이] (지도 970×525 기준).
 */
const SPRITE: Record<string, [number, number, number, number]> = {
    hq: [-29.2, -53.9, 55.9, 62.2], tower: [-20.4, -45.2, 43.6, 42.9]
};
/** 번호 방패는 거점 자리에서 왼쪽으로 조금(게임 화면 실측), 방덱 칸은 그 오른쪽, ⊘ 는 탑 오른쪽 아래 */
const SHIELD_AT: [number, number] = [-20.8, 4.2];
const CUT_AT: [number, number] = [20.9, -11.4];

/**
 * **번호 방패** — swgt.io 시뮬레이터 그림(`/img/siege-icons/`, 22px). 게임처럼 **모양과 색 모두 지금 주인**을 따른다
 * (파랑 방패 · 노랑 네모 · 빨강 원 — 게임 화면 실측), 숫자는 게임 지도 번호(0 은 HQ).
 */
const ICON_SHAPE: Record<number, string> = { 1: 'shield', 2: 'square', 3: 'circle' };
const ICON_COLOR: Record<number, string> = { 1: 'blue', 2: 'yellow', 3: 'red' };
const iconOf = (n: number, ownerSide: number) => `/img/siege-icons/${ICON_SHAPE[ownerSide] || 'shield'}_${label(n)}_${ICON_COLOR[ownerSide] || 'blue'}.png`;

/**
 * 이 거점을 무슨 그림으로 — **본진과 일반 탑 둘뿐**(사용자: 가장 멀쩡한 그림 하나로 통일, 색 셋은 유지). 공사 중은 위 타이머,
 * 남은 방덱은 칸으로 본다. 철장·무너진 탑·대형 탑 그림(`cage`·`ruin`·`big`)은 쓰지 않는다.
 */
function spriteKind(b: MapBase): string {
    return b.type === 1 ? 'hq' : 'tower';
}
/** 탑 가운데 — 고리·흰 테를 두를 자리 */
export const towerCenter = (at: [number, number], k = 1): [number, number] => [at[0], at[1] - 22 * k];

/** 거점 하나 — 게임 그림 · 번호 방패 · 남은 방덱 칸 · 공사 타이머 · 끊김 · 싸움 고리. k 는 폰에서 키우는 배율 */
function BaseMark({ b, at, left, k, color, atkColor, ownerSide, name }: {
    b: MapBase; at: [number, number]; regionSide: number; left: number; k: number; color: string; atkColor: string; ownerSide: number; name: string;
}) {
    if (!at) return null;
    const [x, y] = at;
    const hq = b.type === 1;
    const building = !hq && b.status === 1 && left > 0;
    const kind = spriteKind(b);
    const [dx, dy, w, h] = SPRITE[kind];
    const alive = Math.max(0, b.decks - b.down);
    const [cx, cy] = towerCenter(at, k);
    const sx = x + SHIELD_AT[0] * k, sy = y + SHIELD_AT[1] * k, ss = 22 * k;
    const bw = 40 * k, bh = 5 * k, seg = 5, bx = sx + 9 * k, by = sy - bh / 2 + 1 * k;
    return (
        <g>
            <title>{hq ? `${name} 본진` : `${label(b.n)}번 (전투기록 ${b.n}번) · ${name} · 방덱 ${alive}/${b.decks}`}</title>
            {b.fight > 0 && (
                <circle cx={cx} cy={cy} r={24 * k} fill="none" stroke={atkColor} strokeWidth={3.5} style={{ animation: 'sm-pulse 1s ease-in-out infinite' }} />
            )}
            <image href={`/img/siege-towers/${kind}_${ICON_COLOR[ownerSide] || 'blue'}.webp`}
                x={x + dx * k} y={y + dy * k} width={w * k} height={h * k} />
            {!hq && (
                <g>
                    {/* 남은 방덱 — 게임처럼 어두운 칸에 주인 색 */}
                    <rect x={bx} y={by} width={bw} height={bh} rx={1.2} fill="#2b2124" stroke="#000" strokeWidth={0.6} />
                    {[...Array(seg)].map((_, i) => (
                        <rect key={i} x={bx + (i * bw) / seg + 0.6} y={by + 0.6} width={bw / seg - 1.2} height={bh - 1.2}
                            fill={i < alive ? color : 'transparent'} />
                    ))}
                    <image href={iconOf(b.n, ownerSide)} x={sx - ss / 2} y={sy - ss / 2} width={ss} height={ss} />
                </g>
            )}
            {b.cut === 1 && (
                <g transform={`translate(${x + CUT_AT[0] * k}, ${y + CUT_AT[1] * k}) scale(${k})`}>
                    <circle r={7} fill="rgba(255,255,255,0.85)" stroke="#5b5b5b" strokeWidth={1.8} />
                    <line x1={-4.5} y1={4.5} x2={4.5} y2={-4.5} stroke="#5b5b5b" strokeWidth={1.8} />
                </g>
            )}
            {building && (
                <text x={x} y={y + dy * k - 3} textAnchor="middle" fontSize={12 * k} fontWeight={800} fill="#f3e3c3"
                    stroke="#3b2a1e" strokeWidth={3} paintOrder="stroke">{mmss(left)}</text>
            )}
        </g>
    );
}
